import { EMPTY_FLEET_FILTERS } from '../constants/hardware-inventory';

/*
 * Shared derivations over the fleet hardware inventory shapes.
 *
 * Here rather than in either feature's folder because both the inventory dashboard and the firmware
 * advisory authoring UI ask the same two questions of the fleet endpoint: which filters isolate one
 * component type, and whether a row it returned really is that type. Owning them jointly is the
 * point - a change to either has to be made with both callers in view.
 *
 * Kept dependency-free on purpose: `moment` and `_` are webpack ProvidePlugin globals and are not
 * defined when mocha loads this module, so anything reachable from a test must not rely on them.
 */

const sameValue = (left, right) => {
	return String(left ?? '').toLowerCase() === String(right ?? '').toLowerCase();
};

/*
 * A fleet component row carries no `componentTypeId` — that field exists only on advisories — so
 * the join back to the analytics row is the (category, manufacturer, model) triple. Both endpoints
 * return those normalized and lowercased, which makes the match exact; the comparison is still
 * case-insensitive so a normalization change upstream cannot silently break the callers.
 *
 * This is also the only way to tell whether a component the fleet endpoint returned really is the
 * type that was asked for: `manufacturer` and `model` are partial matches server-side, so a query
 * for model `exos-x16` also comes back with `exos-x16-sas` rows.
 */
export const isSameComponentType = (component, componentType) => {
	if (!component || !componentType) {
		return false;
	}

	return sameValue(componentType.category, component.category)
		&& sameValue(componentType.manufacturer, component.manufacturer)
		&& sameValue(componentType.model, component.model);
};

/*
 * Turns an analytics row into the fleet inventory filters that isolate it. `manufacturer` and
 * `model` are partial matches server-side, but combined with the exact `category` they identify
 * one type in practice. The API escapes `%` and `_` in the value, so a model such as `exos_x16`
 * matches literally instead of treating `_` as a wildcard.
 *
 * Because the match is partial, a caller that walks the returned rows has to re-check each one with
 * `isSameComponentType` before counting it.
 */
export const toFleetFilters = (componentType) => {
	return {
		...EMPTY_FLEET_FILTERS,
		category: componentType?.category ?? '',
		manufacturer: componentType?.manufacturer ?? '',
		model: componentType?.model ?? ''
	};
};

/*
 * The catalog is an inner join on the fleet analytics table, so a component type that no appliance
 * currently reports is legitimately absent from it. Returning null rather than undefined keeps the
 * "not in the catalog" case explicit for callers that have to render something regardless.
 */
/*
 * The version HITS has endorsed for this component type — `approved_firmware_version` on the
 * component type, copied onto the analytics row (spec D18).
 *
 * Every read of that field goes through here rather than dereferencing it inline, because it is the
 * one field on these shapes that is being renamed underneath us: SYN-2800 replaces
 * `lastCuratedVersion` with `approvedFirmwareVersion` in a single cut, with no dual naming and no
 * deprecation window. Funnelling the reads makes that a one-line change here instead of a sweep
 * through both features.
 *
 * Do not confuse it with curation: `firmware_advisory` curation *warns* that a version is bad,
 * this *endorses* the version a type should run. D18 binds the two words apart, so neither the
 * field nor any copy derived from it says "curated".
 */
export const approvedVersionOf = (componentType) => {
	return componentType?.approvedFirmwareVersion ?? null;
};

export const findComponentTypeById = (componentTypeId, componentTypes) => {
	if (!componentTypeId) {
		return null;
	}

	return (componentTypes ?? []).find((componentType) => {
		return componentType?.id === componentTypeId;
	}) ?? null;
};
